// Web port of the native cloudRecordingAllowed discipline
// (core/{ios,android}/.../RecordingEntitlement). Tri-state on purpose: an ABSENT
// field is "unknown", NOT "deny" — the picker must never hide a mode or block cloud
// on the strength of entitlements that simply were not reported.

import type { EffectiveEntitlementExplain, PlanEntitlements } from '../../../lib/api';

/**
 * Whether the plan permits CLOUD as a recording location.
 *   null  = unknown (locations field absent / entitlements not loaded)
 *   false = present without "cloud", or recording explicitly disabled
 *   true  = present and contains "cloud"
 * Mirror of the native helper — do NOT collapse the unknown state into deny.
 */
export function cloudRecordingAllowed(e: PlanEntitlements | undefined | null): boolean | null {
  const locs = e?.locations;
  if (locs == null) return null; // unknown, NOT deny
  if (e?.recording_enabled === false) return false;
  return locs.some((l) => l.toLowerCase() === 'cloud');
}

/**
 * Allowed record MODES for R3, lower-cased. Tri-state:
 *   null = "no constraint" → show the full static list (not fetched / recording_modes absent / empty)
 *   []-derived list = only these modes are plan-allowed
 * A non-empty recording_modes list constrains the picker even when complete===false: right after
 * attaching, the decision is often provisional (some non-recording sources unreported), but the
 * plan's allowed recording modes are already known and SHOULD constrain (same recording_modes the
 * "switch-to-cloud picks a default mode" path uses — keep the two consistent). Only absent/empty
 * recording_modes (truly unknown) leaves the picker unconstrained — the cloudRecordingAllowed
 * "unknown is not deny" discipline.
 */
export function planRecordingModes(explain: EffectiveEntitlementExplain | null): string[] | null {
  if (!explain) return null; // not fetched / failed → unknown
  const modes = explain.entitlements?.recording_modes;
  if (!Array.isArray(modes) || modes.length === 0) return null; // absent/empty → unknown
  return modes.map((m) => m.toLowerCase());
}
